import { jsxs, Fragment, jsx } from 'react/jsx-runtime';
import { Label } from '@wds/label';
import { deprecate, uniqueId, safeRest, defaultFonts } from '@wds/utils';
import { useMemo } from 'react';
import { createMemoStyles, getColor } from '@wds/styles';

var COLOR_OPTIONS = ['default', 'success', 'warning', 'error'];
var ALIGN_OPTIONS = ['top', 'center', 'bottom'];

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol, Iterator */


var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

var useStylesInput$1 = createMemoStyles(function (theme) {
  var radius = theme.radius,
    palette = theme.palette,
    shadow = theme.shadow,
    border = theme.border;
  var inputGroupStyles = {
    wrenchInputGroup: {
      boxSizing: 'border-box',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      border: border.main,
      background: palette.background.light,
      borderRadius: radius.small,
      width: function (_a) {
        var stretched = _a.stretched;
        return stretched ? '100%' : null;
      },
      pointerEvents: function (_a) {
        var disabled = _a.disabled;
        return disabled ? 'none' : null;
      },
      opacity: function (_a) {
        var disabled = _a.disabled;
        return disabled ? '0.4' : null;
      },
      borderColor: function (_a) {
        var color = _a.color;
        return getColor(palette, color, {
          fallback: palette.border.main
        });
      },
      '&:hover': {
        borderColor: function (_a) {
          var color = _a.color;
          return getColor(palette, color, {
            fallback: palette.border.hover
          });
        },
        boxShadow: shadow.sky,
        transition: ['border-color 100ms', 'box-shadow 100ms']
      },
      '&:has(input:focus), &:has(textarea:focus)': {
        borderColor: function (_a) {
          var color = _a.color;
          return getColor(palette, color, {
            fallback: palette.border.active
          });
        },
        transition: ['border-color', '100ms']
      },
      '& > input, & > textarea': {
        color: function (_a) {
          var color = _a.color;
          return getColor(palette, color, {
            fallback: palette.text.dark
          });
        },
        border: 'none',
        flexGrow: 1,
        width: function (_a) {
          var stretched = _a.stretched;
          return stretched ? '100%' : null;
        },
        '&:hover': {
          boxShadow: 'none'
        }
      },
      '&:empty': {
        border: 0,
        clip: 'rect(1px, 1px, 1px, 1px)',
        height: '1px',
        margin: '-1px',
        overflow: 'hidden',
        padding: 0,
        position: 'absolute',
        width: '1px'
      }
    }
  };
  return inputGroupStyles;
});

var InputGroup = function InputGroup(_a) {
  var children = _a.children,
    id = _a.id,
    label = _a.label,
    _b = _a.stretched,
    stretched = _b === void 0 ? false : _b,
    _c = _a.disabled,
    disabled = _c === void 0 ? false : _c,
    _d = _a.color,
    color = _d === void 0 ? 'default' : _d,
    rest = __rest(_a, ["children", "id", "label", "stretched", "disabled", "color"]);
  deprecate({
    color: color
  }, 'color', 'warning', 'InputGroup');
  var inputID = useMemo(function () {
    return id !== null && id !== void 0 ? id : uniqueId('input');
  }, [id]);
  var wrenchInputGroup = useStylesInput$1({
    color: color,
    stretched: stretched,
    disabled: disabled
  }).wrenchInputGroup;
  return jsxs(Fragment, {
    children: [label && jsx(Label, {
      htmlFor: inputID,
      children: label
    }), jsx("div", __assign({
      className: wrenchInputGroup
    }, safeRest(rest), {
      children: children
    }))]
  });
};

var useStylesInput = createMemoStyles(function (theme) {
  var palette = theme.palette,
    spacing = theme.spacing;
  var inputGroupStyles = {
    wrenchInputGroupAddon: __assign(__assign({}, defaultFonts(theme)), {
      whiteSpace: 'nowrap',
      display: 'flex',
      alignSelf: function (_a) {
        var align = _a.align;
        if (align === 'bottom') return 'flex-end';
        if (align === 'top') return 'flex-start';
        return 'center';
      },
      marginTop: function (_a) {
        var align = _a.align;
        return align === 'top' ? spacing[1] : 0;
      },
      marginBottom: function (_a) {
        var align = _a.align;
        return align === 'bottom' ? spacing[1] : 0;
      },
      // if the children are SVGs, then add even more margin
      '& > svg': {
        marginTop: function (_a) {
          var align = _a.align;
          return align === 'top' ? spacing[2] : 0;
        },
        marginBottom: function (_a) {
          var align = _a.align;
          return align === 'bottom' ? spacing[2] : 0;
        }
      },
      '&:first-child': {
        paddingLeft: spacing[1],
        '& > svg': {
          marginLeft: spacing[2],
          fill: function (_a) {
            var color = _a.color;
            return getColor(palette, color, {
              fallback: palette.text.dark
            });
          },
          color: function (_a) {
            var color = _a.color;
            return getColor(palette, color, {
              fallback: palette.text.dark
            });
          }
        }
      },
      '&:last-child': {
        paddingRight: spacing[1],
        '& > svg': {
          marginRight: spacing[2],
          fill: function (_a) {
            var color = _a.color;
            return getColor(palette, color, {
              fallback: palette.text.dark
            });
          },
          color: function (_a) {
            var color = _a.color;
            return getColor(palette, color, {
              fallback: palette.text.dark
            });
          }
        }
      }
    })
  };
  return inputGroupStyles;
});

var InputGroupAddon = function InputGroupAddon(_a) {
  var children = _a.children,
    _b = _a.color,
    color = _b === void 0 ? 'default' : _b,
    _c = _a.align,
    align = _c === void 0 ? 'center' : _c,
    rest = __rest(_a, ["children", "color", "align"]);
  var wrenchInputGroupAddon = useStylesInput({
    color: color,
    align: align
  }).wrenchInputGroupAddon;
  return jsx("div", __assign({
    className: wrenchInputGroupAddon
  }, safeRest(rest), {
    children: children
  }));
};

export { ALIGN_OPTIONS, COLOR_OPTIONS, InputGroup, InputGroupAddon };
